import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Box, Button, Typography } from '@mui/material';
import Async from 'components/Async';
import Comment from 'components/Comment';
import MediaTypography from 'components/MediaTypography';
import StudentRubric from 'components/StudentRubric';

import ServiceRouter, { useServiceRouter } from '@common/components/ServiceRouter';
import { IStudentLessonModel } from '@common/models/StudentLessons/Studentlesson';

import RubricModal from './RubricModal';
import { StyledLessonDescriptionCommentList, StyledLessonDescriptionCommentWrap } from './styled';

interface PropsType {
	lessonModel: IStudentLessonModel;
}

function LessonDescriptionTeacherComment({ lessonModel }: PropsType) {
	const { t } = useTranslation();
	const { checkService } = useServiceRouter();
	const [loading, setLoading] = useState(false);
	const [isRubricModalOpen, setIsRubricModalOpen] = useState(false);

	useEffect(() => {
		const fetchQuestions = async () => {
			try {
				setLoading(true);
				await lessonModel.gradebook?.fetchQuestions();
			} finally {
				setLoading(false);
			}
		};

		checkService('international') && fetchQuestions();
	}, []);

	return (
		<StyledLessonDescriptionCommentWrap>
			{!!lessonModel.gradesWithComment?.length &&
				lessonModel.gradesWithComment.map((grade, index) => (
					<StyledLessonDescriptionCommentList key={index}>
						<Comment
							name={grade.fullName || t('lesson.teacherFeedback')}
							date={grade.created_at}
							text={grade.comment}
						/>
						{grade.reject_comment && (
							<Comment
								name={
									grade.fullName
										? `${grade.fullName} (rejected)`
										: `${t('lesson.teacherFeedback')} (rejected)`
								}
								date={grade.rejected_at}
								text={grade.reject_comment}
							/>
						)}
					</StyledLessonDescriptionCommentList>
				))}
			{!!lessonModel.gradebook?.comment_answers?.length && (
				<Async loading={loading}>
					<Comment
						name={lessonModel?.grades?.[0]?.fullName || t('lesson.teacherFeedback')}
						date={lessonModel.gradebook.updated_at}
						withOutText
						text=""
					/>
					{lessonModel.gradebook?.commentQuestions?.map((question: any, index: any) => (
						<StyledLessonDescriptionCommentList key={index}>
							<Comment
								name={question.label}
								date=""
								text={lessonModel.gradebook?.comment_answers[index]}
							/>
						</StyledLessonDescriptionCommentList>
					))}
					<ServiceRouter
						international={
							!!lessonModel.rubricLevels?.length && (
								<Box display="flex" flexDirection="column" gap="4px">
									<Comment name={t('program.nonGradedAssignment')} withOutText date="" text="" />

									{lessonModel.rubricLevels?.map((currentRubricLevel) => {
										const allRubrics = lessonModel?.rubricUnions.flatMap(
											(rubricUnion) => rubricUnion.rubrics,
										);

										const currentRubric = allRubrics.find((rubric) =>
											rubric.rubricLevels?.some(
												(rubricLevel) => rubricLevel.id === currentRubricLevel?.id,
											),
										);

										return (
											<Box display="flex" flexDirection="column" gap="4px" marginBottom="8px">
												<MediaTypography variant="body2">{currentRubric?.title}</MediaTypography>
												<Box
													key={currentRubricLevel.id}
													display="flex"
													flexDirection="column"
													gap="8px"
												>
													<StudentRubric rubricLevel={currentRubricLevel} />
												</Box>
											</Box>
										);
									})}

									<Button
										variant="text"
										endIcon={<ArrowOutwardIcon />}
										onClick={() => setIsRubricModalOpen(true)}
										aria-haspopup="dialog"
										aria-expanded={isRubricModalOpen}
										sx={{
											alignSelf: 'flex-start',
											minWidth: 0,
											padding: 0,
											textDecoration: 'underline',
										}}
									>
										{t('lesson.rubric.showMore')}
									</Button>

									<RubricModal
										lessonModel={lessonModel}
										open={isRubricModalOpen}
										onClose={() => setIsRubricModalOpen(false)}
									/>
								</Box>
							)
						}
					/>
				</Async>
			)}
			{!lessonModel.gradesWithComment?.length &&
				!lessonModel.gradebook?.comment_answers?.length && (
					<Typography>{t('lesson.emptyTeacherComment')}</Typography>
				)}
		</StyledLessonDescriptionCommentWrap>
	);
}

export default LessonDescriptionTeacherComment;
